import { CurbZoneMapMode, CurbZonePageModeType } from "@/modules/curb-zone/curb-zone.type";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Dispatch, SetStateAction } from "react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

import { Button } from "@/components/ui/button";
import { CurbArea } from "types/curb-area.type";
import { FeatureCollection } from "geojson";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

interface ModifyCurbAreaPromptProps {
  appMode: CurbZoneMapMode;
  setOpenCurbAreaModal: VoidFunction;
  setCurbAreaEditionData: Dispatch<SetStateAction<FeatureCollection>>;
  curbAreaEditionData: FeatureCollection;
  curbAreaDetail?: CurbArea | null;
  saveCurbAreaProperties: (promptedProperties: CurbAreaProperties) => Promise<void>;
  cancelCurbAreaChange: VoidFunction;
}

const formSchema = z.object({
  name: z.string().trim().min(1, { message: "Name is required" }),
  note: z.string().optional(),
  cityId: z.string(),
  publishedDate: z.date().optional(),
});

export type CurbAreaProperties = z.infer<typeof formSchema>;

export const ModifyCurbAreaPrompt = ({
  appMode,
  curbAreaDetail,
  setOpenCurbAreaModal,
  curbAreaEditionData,
  setCurbAreaEditionData,
  cancelCurbAreaChange,
  saveCurbAreaProperties,
}: ModifyCurbAreaPromptProps) => {
  const form = useForm<CurbAreaProperties>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: curbAreaDetail?.name ?? "",
      note: curbAreaDetail?.note ?? "",
      cityId: curbAreaDetail?.cityId ?? "",
    },
    mode: "all",
  });

  const submit = async (formValues: CurbAreaProperties) => {
    await saveCurbAreaProperties({
      ...formValues,
      publishedDate: formValues.publishedDate,
    });
  };

  const handleClose = () => {
    if (appMode === CurbZonePageModeType.AddCurbArea) {
      setCurbAreaEditionData({
        ...curbAreaEditionData,
        features: curbAreaEditionData.features.slice(0, -1),
      });
    }
    cancelCurbAreaChange();
    setOpenCurbAreaModal();
    form.reset();
  };

  return (
    <Dialog open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`${appMode === CurbZonePageModeType.AddCurbArea ? "Add a new" : "Edit a"} curb area`}</DialogTitle>
          <DialogDescription>Curb area information</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="sm:justify-between">
              <Button type="button" variant="secondary" onClick={handleClose}>
                Close
              </Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
